import * as React from "react";
import {
  ContractorPackage,
  ContractorPackageLineItem,
} from "@interfaces/contractorPackage";
import {
  decodeContractorPackage,
  encodeContractorPackage,
} from "decoders/contractorPackage";
import { getApiData, listApiData, postApiData } from "@hooks/utils/api";
import { ToastOptions, toast } from "react-toastify";

export const useContractorPackage = (
  projectId?: string,
  packageId?: string
) => {
  const [contractorPackages, setContractorPackages] = React.useState<
    ContractorPackageLineItem[]
  >([]);
  const [isContractorPackageLoading, setIsLoading] =
    React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const getContractorPackage = async (
    id: string
  ): Promise<ContractorPackageLineItem | undefined> => {
    try {
      setIsLoading(true);
      const contractorPackageResult = await getApiData(
        "getContractorPackage",
        "contractorPackage",
        id
      );
      if (Object.keys(contractorPackageResult.data).length === 0) {
        return undefined;
      }
      return contractorPackageResult
        ? decodeContractorPackage(
            contractorPackageResult.data as ContractorPackage
          )
        : undefined;
    } catch (e: any) {
      setError("Could not fetch contractorPackage");
    } finally {
      setIsLoading(false);
    }
  };

  const getContractorPackageForUser = async (
    id: string
  ): Promise<ContractorPackageLineItem | undefined> => {
    try {
      setIsLoading(true);
      const getContractorPackageForUserResult = await getApiData(
        "getContractorPackageForUser",
        "contractorPackage",
        id
      );
      if (Object.keys(getContractorPackageForUserResult.data).length === 0) {
        return undefined;
      }
      return getContractorPackageForUserResult
        ? decodeContractorPackage(
            getContractorPackageForUserResult.data as ContractorPackage
          )
        : undefined;
    } catch (e: any) {
      setError("Could not fetch contractorPackage");
    } finally {
      setIsLoading(false);
    }
  };

  const listContractorPackages = async (projectId: string): Promise<void> => {
    try {
      setIsLoading(true);
      const contractorPackageResult = await listApiData(
        "listContractorPackages",
        "contractorPackage",
        projectId
      );
      const packageLineItems = await Promise.all(
        contractorPackageResult.data.map((p) =>
          decodeContractorPackage(p as ContractorPackage)
        )
      );
      setContractorPackages(packageLineItems);
    } catch (e: any) {
      setError("Could not list contractorPackages");
    } finally {
      setIsLoading(false);
    }
  };

  const searchContractorPackages = async (
    query: string
  ): Promise<ContractorPackageLineItem[]> => {
    try {
      const contractorPackageResult = await listApiData(
        "searchContractorPackages",
        "contractorPackage",
        query
      );
      return await Promise.all(
        contractorPackageResult.data.map((p) =>
          decodeContractorPackage(p as ContractorPackage)
        )
      );
    } catch (e: any) {
      setError("Could not search contractorPackages");
      return [];
    }
  };

  const createContractorPackage = async (
    contractorPackage: ContractorPackageLineItem
  ): Promise<ContractorPackageLineItem> => {
    try {
      setIsLoading(true);
      const encodedContractorPackage =
        encodeContractorPackage(contractorPackage);
      const upsertedContractorPackage = await postApiData(
        "createContractorPackage",
        "contractorPackage",
        encodedContractorPackage
      );
      const decodedContractorPackage = await decodeContractorPackage(
        upsertedContractorPackage.data as ContractorPackage
      );
      setContractorPackages([...contractorPackages, decodedContractorPackage]);
      toast("Package created.", {
        type: "success",
      } as ToastOptions);
      return decodedContractorPackage;
    } catch (e: any) {
      toast("Could not create package!", {
        type: "error",
      } as ToastOptions);
      setError("Could not list contractorPackages");
    } finally {
      setIsLoading(false);
    }
    return contractorPackage;
  };

  const updateContractorPackage = async (
    contractorPackage: ContractorPackageLineItem
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedContractorPackage =
        encodeContractorPackage(contractorPackage);
      await postApiData(
        "updateContractorPackage",
        "contractorPackage",
        encodedContractorPackage
      );
      setContractorPackages(
        contractorPackages.map((c) => {
          if (c.id === contractorPackage.id) {
            return contractorPackage;
          }
          return c;
        })
      );
      toast("Package updated.", {
        type: "success",
      } as ToastOptions);
    } catch (e: any) {
      toast("Could not update package!", {
        type: "error",
      } as ToastOptions);
      setError("Could not update contractorPackage");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteContractorPackage = async (
    contractorPackage: ContractorPackageLineItem
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedContractorPackage =
        encodeContractorPackage(contractorPackage);
      await postApiData(
        "deleteContractorPackage",
        "contractorPackage",
        encodedContractorPackage
      );
      setContractorPackages(
        contractorPackages.filter((p) => p.id !== contractorPackage.id)
      );
      toast("Package deleted.", {
        type: "success",
      } as ToastOptions);
    } catch (e: any) {
      toast("Could not delete package!", {
        type: "error",
      } as ToastOptions);
      setError("Could not delete contractorPackage");
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (projectId) {
      listContractorPackages(projectId);
    }
  }, [projectId]);

  React.useEffect(() => {
    if (packageId) {
      listContractorPackages(packageId);
    }
  }, [packageId]);

  return {
    contractorPackages,
    getContractorPackage,
    getContractorPackageForUser,
    listContractorPackages,
    createContractorPackage,
    updateContractorPackage,
    deleteContractorPackage,
    searchContractorPackages,
    isContractorPackageLoading,
    error,
  };
};
